<template>
  <v-container>
    <div v-if="loading" class="text-center py-16">
      <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
    </div>

    <div v-else-if="users.length === 0" class="empty-state">
      <v-icon size="80" class="mb-4" color="white">mdi-human-male-female</v-icon>
      <h3 class="text-h6 font-weight-medium text-center white--text">Gerade ist niemand hier</h3>
      <p class="text-center body-1 white--text px-4 mt-2">Sei der Erste, der im Profil die Sichtbarkeit für den Pool aktiviert!</p>
    </div>

    <transition-group name="card-fade" tag="div" class="row justify-center">
      <v-col v-for="(user, index) in users" :key="user._id" cols="12" sm="10" md="8" lg="6" class="card-wrapper">
        <v-card class="user-card" dark>
          <v-img
            :src="user.bild ? 'https://app.wellcomefit.ch/img/profilbilder/' + user.bild : '/img/profilbilder/default.png'"
            height="350px"
            class="white--text align-end image-gradient cursor-pointer"
            @click="$emit('view-profile', user._id)">
            <div class="card-title-wrapper pa-4">
              <h2 class="font-weight-bold text-h5">
                {{ user.vorname }}<span v-if="user.age">, {{ user.age }}</span>
              </h2>
              <p class="mb-0">@{{ user.username }}</p>
            </div>
          </v-img>

          <v-card-text class="pt-4">
            <div v-if="user.isLimited">
              <p v-if="user.privacySetting === 'private'"><v-icon small left>mdi-lock-outline</v-icon> Dies ist ein privates Profil.</p>
              <p v-else><v-icon small left>mdi-account-group-outline</v-icon> Werde ein Freund, um mehr zu sehen!</p>
            </div>

            <div v-else>
              <p v-if="user.bio" class="body-1 mb-5">{{ user.bio }}</p>

              <v-row dense class="text-center mb-4">
                <v-col>
                  <v-icon large color="primary">mdi-clock-outline</v-icon>
                  <div class="caption mt-1">Checked-in</div>
                  <div class="font-weight-bold">vor {{ formatCheckinTime(user.checkinDate) }}</div>
                </v-col>
                <v-col v-if="user.trainingGoals && user.trainingGoals.length">
                  <v-icon large color="primary">mdi-flag-checkered</v-icon>
                  <div class="caption mt-1">Trainingsziel</div>
                  <div class="font-weight-bold">{{ user.trainingGoals[0] }}</div>
                </v-col>
              </v-row>

              <div v-if="user.interests && user.interests.length > 0">
                <v-card-subtitle class="px-0">Interessen</v-card-subtitle>
                <v-chip-group column>
                  <v-chip v-for="interest in user.interests" :key="interest" class="ma-1" color="primary">
                    <v-icon left small>{{ getInterestIcon(interest) }}</v-icon>
                    {{ interest }}
                  </v-chip>
                </v-chip-group>
              </div>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-btn v-if="!user.relationshipStatus" color="primary" block large @click="sendFriendRequest(user, index)">
              <v-icon left>mdi-account-plus</v-icon> Anfreunden
            </v-btn>
            <v-btn v-else-if="user.relationshipStatus === 'pending_outgoing'" block large disabled text>
              <v-icon left>mdi-clock-outline</v-icon> Angefragt
            </v-btn>
            <div v-else-if="user.relationshipStatus === 'pending_incoming'" class="d-flex" style="width: 100%">
              <v-btn large text color="error" class="flex-grow-1" @click="handleRelationship(user._id, 'decline', index)">Ablehnen</v-btn>
              <v-btn large color="success" class="flex-grow-1" @click="handleRelationship(user._id, 'accept', index)">Annehmen</v-btn>
            </div>
            <v-btn v-else-if="user.relationshipStatus === 'accepted'" block large text disabled color="success">
              <v-icon left>mdi-check-circle-outline</v-icon> Freunde
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </transition-group>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import api from "@/api"
import { formatDistanceToNow } from "date-fns"
import { de } from "date-fns/locale"

export default {
  name: "CheckedInPoolComponent",
  data: () => ({
    loading: true,
    users: [],
    snackbar: { show: false, text: "", color: "" },
  }),
  methods: {
    async fetchCheckedInUsers() {
      this.loading = true
      try {
        const response = await api.social.getCheckedInUsers()
        // Wir filtern geblockte User direkt aus, da sie nicht interagierbar sein sollen
        this.users = response.filter((u) => u.relationshipStatus !== "blocked")
      } catch (error) {
        console.error("Fehler beim Laden der eingecheckten User:", error)
        this.showSnackbar("Daten konnten nicht geladen werden.", "error")
      } finally {
        this.loading = false
      }
    },

    async sendFriendRequest(user, index) {
      try {
        await api.social.sendFriendRequest(user._id)
        this.showSnackbar("Freundschaftsanfrage gesendet!", "success")
        // UI sofort aktualisieren
        this.$set(this.users, index, { ...user, relationshipStatus: "pending_outgoing" })
      } catch (error) {
        this.showSnackbar(error.response?.data?.message || "Aktion fehlgeschlagen", "error")
      }
    },

    async handleRelationship(userId, action, index) {
      try {
        let responseMessage = ""
        if (action === "accept") {
          await api.social.acceptFriendRequest(userId) // Annahme: Diese API-Route existiert
          responseMessage = "Freundschaft angenommen!"
          this.$set(this.users, index, { ...this.users[index], relationshipStatus: "accepted" })
        } else {
          await api.social.declineFriendRequest(userId) // Annahme: Diese API-Route existiert
          responseMessage = "Anfrage abgelehnt."
          this.$set(this.users, index, { ...this.users[index], relationshipStatus: null })
        }
        this.showSnackbar(responseMessage, "success")
      } catch (error) {
        this.showSnackbar(error.response?.data?.message || "Aktion fehlgeschlagen", "error")
      }
    },

    formatCheckinTime(dateString) {
      if (!dateString) return ""
      // Liefert direkt den formatierten String wie "vor 5 Minuten"
      return formatDistanceToNow(new Date(dateString), { addSuffix: false, locale: de })
    },

    getInterestIcon(interest) {
      const lowerInterest = interest.toLowerCase()
      // Mapping von Interessen zu Material Design Icons
      const iconMap = {
        kraftsport: "mdi-weight-lifter",
        cardio: "mdi-run-fast",
        joggen: "mdi-run-fast",
        yoga: "mdi-yoga",
        crossfit: "mdi-kettlebell",
        bodybuilding: "mdi-arm-flex",
        fitness: "mdi-heart-pulse",
        schwimmen: "mdi-swim",
        radfahren: "mdi-bike",
      }
      return iconMap[lowerInterest] || "mdi-star" // Standard-Icon
    },

    showSnackbar(text, color) {
      this.snackbar.text = text
      this.snackbar.color = color
      this.snackbar.show = true
    },
  },
  created() {
    this.fetchCheckedInUsers()
  },
}
</script>

<style scoped>
/* Animierter Fade-In für die Karten */
.card-fade-enter-active,
.card-fade-leave-active {
  transition: all 0.5s ease;
}
.card-fade-enter,
.card-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.user-card {
  background: #1e1e1e;
  border-radius: 16px !important; /* Abgerundete Ecken */
  overflow: hidden; /* Wichtig für abgerundete Ecken bei v-img */
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.user-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.5);
}

/* Lebendiger Zoom-Effekt für das Bild */
.user-card .v-image {
  transition: transform 0.5s ease;
}
.user-card:hover .v-image {
  transform: scale(1.05);
}

.image-gradient::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 60%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent);
}

.card-title-wrapper {
  position: relative;
  z-index: 2;
}

.cursor-pointer {
  cursor: pointer;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 112px); /* Anpassen je nach deiner App-Bar-Höhe */
  padding: 1rem;
}
</style>
